import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import { PAGE_CHROME_LOADER_BRAND_GREEN } from '../constants/pageChrome';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'member' | 'trainer';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { user, profile, loading, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [waitingForProfile, setWaitingForProfile] = useState(false);
  const [profileTimeout, setProfileTimeout] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const hasRedirectedRef = useRef(false);
  const hasLoggedRef = useRef(false);
  const wasVisibleRef = useRef(true);
  const hasInitialProfileRef = useRef(false);

  // Track if we've ever had a profile (to prevent showing loading on visibility changes)
  useEffect(() => {
    if (profile) {
      hasInitialProfileRef.current = true;
    }
  }, [profile]);

  // Track visibility changes to prevent showing loading on tab switch/screen lock
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasVisibleRef.current = false;
      } else if (document.visibilityState === 'visible') {
        wasVisibleRef.current = true;
        // If we had a profile before and it's missing now, it's likely just a visibility change
        // Don't show loading in this case
        if (hasInitialProfileRef.current && !profile && user) {
          console.log('⏭️ Visibility change detected - profile was loaded before, skipping loading state');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [profile, user]);

  // Retry profile fetch if user is authenticated but profile is missing
  // If requiredRole is set, we MUST wait for profile to load
  useEffect(() => {
    // If we've had a profile before and it's missing now, it's likely just a temporary state
    // Don't show loading on visibility changes (unless requiredRole is set)
    if (hasInitialProfileRef.current && !profile && user && !requiredRole) {
      console.log('⏭️ Profile was loaded before but missing now - likely visibility change, skipping wait');
      setWaitingForProfile(false);
      return;
    }

    // If requiredRole is set, we MUST have a profile - always wait for it
    // Otherwise, only wait on first load
    if (user && !profile && !loading) {
      if (requiredRole || !hasInitialProfileRef.current) {
        // Start waiting for profile
        setWaitingForProfile(true);
        setProfileTimeout(false);
        hasRedirectedRef.current = false;
        hasLoggedRef.current = false;
        
        // Timeout - if profile doesn't load, show error
        const timeout = setTimeout(() => {
          console.warn('⚠️ Profile still not loaded after 8 seconds');
          setProfileTimeout(true);
          setWaitingForProfile(false);
        }, 8000); // 8 seconds timeout
        
        return () => clearTimeout(timeout);
      }
    } else if (profile) {
      setWaitingForProfile(false);
      setProfileTimeout(false);
      hasRedirectedRef.current = false;
      hasLoggedRef.current = false;
    }
  }, [user, profile, loading, requiredRole]);

  // Handle redirect after showing error message
  useEffect(() => {
    if (shouldRedirect && redirectPath) {
      const timer = setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 2000); // Show message for 2 seconds before redirecting
      
      return () => clearTimeout(timer);
    }
  }, [shouldRedirect, redirectPath, navigate]);

  // Show loading spinner only for initial auth check, not for profile loading
  // Allow access even if profile isn't loaded yet
  if (loading) {
    return (
      <LoadingSpinner
        fullScreen
        // Match iOS `BravosSessionGateView`: always green during bootstrap.
        statusBarBackground={PAGE_CHROME_LOADER_BRAND_GREEN}
      />
    );
  }
  
  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/auth?mode=login" state={{ from: location }} replace />;
  }

  // If a requiredRole is specified, we MUST have a profile to check access
  // Wait for profile to load before allowing or denying access
  if (requiredRole) {
    // If profile is not loaded yet, show loading or error
    if (!profile) {
      // If timeout occurred, show error
      if (profileTimeout) {
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Oops, something went wrong</h1>
              <p className="text-gray-600 mb-6">
                We couldn't load your profile. Please try refreshing the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Refresh Page
              </button>
            </div>
          </div>
        );
      }
      
      // Still waiting for profile - show loading
      return <LoadingSpinner fullScreen statusBarBackground={PAGE_CHROME_LOADER_BRAND_GREEN} />;
    }
    
    // Profile is loaded, check if role matches
    if (profile.role !== requiredRole) {
      // Determine correct dashboard and set up redirect
      const correctDashboard = profile.role === 'trainer' 
        ? '/trainer-dashboard' 
        : '/member-dashboard';
      
      // Set up redirect if not already set
      if (!shouldRedirect) {
        setShouldRedirect(true);
        setRedirectPath(correctDashboard);
      }
      
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Oops, something went wrong</h1>
            <p className="text-gray-600 mb-6">
              You don't have access to this page.
            </p>
          </div>
        </div>
      );
    }
  }

  // If no requiredRole, allow access (profile can load in background)
  // If requiredRole is set and we got here, role matches - allow access
  return <>{children}</>;
}; 